import _ from 'lodash';
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel
} from '@mui/material';
import authService from '../../api-authorization/AuthorizeService';
import styles from './AdminConfigTractorNozzles.module.css';
import AddNozzleDialog from './AddNozzleDialog/AddNozzleDialog';
import DeleteNozzleDialog from './DeleteNozzleDialog/DeleteNozzleDialog';

const AdminConfigTractorNozzles = () => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tractor, setTractor] = useState({});
  const [nozzles, setNozzles] = useState([]);
  const [booms, setBooms] = useState([]);
  const [fetchedNozzles, setFetchedNozzles] = useState([]);
  const [nozzleForDeletion, setNozzleForDeletion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(false);
  const [addNozzleDialogOpen, setIsAddNozzleDialogOpen] = useState(false);
  const [deleteNozzleDialogOpen, setIsDeleteNozzleDialogOpen] = useState(false);

  const getClientId = useCallback(
    () => searchParams.get('clientId'),
    [searchParams]
  );

  const getTractorId = useCallback(
    () => searchParams.get('tractorId'),
    [searchParams]
  );

  const fetchTractor = useCallback(async () => {
    setLoading(true);
    try {
      const clientId = getClientId();
      const tractorId = getTractorId();
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/tractor?clientId=${clientId}&tractorId=${tractorId}`,
        {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        }
      );
      const tractor = await response.json();
      setTractor(tractor);
      setLoading(false);
      setError(false);
    } catch (error) {
      setTimeout(() => {
        setError(true);
        setLoading(false);
      }, 1000);
    }
  }, [getClientId, getTractorId]);

  const fetchTractorBooms = useCallback(async () => {
    setLoading(true);
    try {
      const clientId = getClientId();
      const tractorId = getTractorId();
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/booms?clientId=${clientId}&tractorId=${tractorId}`,
        {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        }
      );
      const booms = await response.json();
      setBooms(booms);
      setLoading(false);
      setError(false);
    } catch (error) {
      setTimeout(() => {
        setError(true);
        setLoading(false);
      }, 1000);
    }
  }, [getClientId, getTractorId]);

  const fetchNozzles = useCallback(async () => {
    setLoading(true);
    try {
      const clientId = getClientId();
      const tractorId = getTractorId();
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/nozzles?clientId=${clientId}&tractorId=${tractorId}`,
        {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        }
      );
      const nozzles = (await response.json()).map((n) => ({
        ...n,
        nozzleAngle: String(n.nozzleAngle ?? ''),
        nozzleGpm: String(n.nozzleGpm ?? ''),
        sprayWidth: String(n.sprayWidth ?? ''),
        overlap: String(parseFloat((n.overlap * 100).toFixed(2)))
      }));
      setFetchedNozzles(nozzles);
      setNozzles(nozzles);
      setLoading(false);
      setError(false);
    } catch (error) {
      setTimeout(() => {
        setError(true);
        setLoading(false);
      }, 1000);
    }
  }, [getClientId, getTractorId]);

  useEffect(() => {
    fetchTractor();
    fetchNozzles();
    fetchTractorBooms();
  }, [getClientId, getTractorId, fetchTractor, fetchNozzles]);

  const updateNozzle = (nozzleId, fieldName, value) => {
    setNozzles(prev =>
      prev.map(n => n.id === nozzleId ? { ...n, [fieldName]: value } : n)
    );
  };

  const hasNozzleValueChanged = (nozzle, fieldName) => {
    const fetchedNozzle = fetchedNozzles.find(
      (fetchedNozzle) => fetchedNozzle.id === nozzle.id
    );
    return fetchedNozzle[fieldName] !== nozzle[fieldName];
  };

  const haveAnyValuesChanged = () => {
    return _.isEqual(fetchedNozzles, nozzles);
  };

  const discardChanges = () => {
    setNozzles(fetchedNozzles);
  };

  const updateNozzles = async () => {
    const token = await authService.getAccessToken();

    const body = nozzles.map((n) => ({
      ...n,
      nozzleAngle: Math.max(0, parseFloat(n.nozzleAngle) || 0),
      nozzleGpm: Math.max(0, parseFloat(n.nozzleGpm) || 0),
      sprayWidth: Math.max(0, parseFloat(n.sprayWidth) || 0),
      overlap: Math.min(100, Math.max(0, parseFloat(n.overlap) || 0)) / 100
    }));
    await fetch(
      `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/nozzles`,
      {
        method: 'POST',
        headers: !token
          ? {}
          : {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        body: JSON.stringify(body)
      }
    );
    toast.success('Nozzles updated successfully!');
    fetchNozzles();
  };

  const openAddNozzleDialog = () => {
    setIsAddNozzleDialogOpen(true);
  };

  const closeShowAddNozzleDialog = () => {
    setIsAddNozzleDialogOpen(false);
  };

  const handleAddNozzlesSuccess = () => {
    setIsAddNozzleDialogOpen(false);
    fetchNozzles();
  };

  const openDeleteNozzleDialog = (nozzle) => {
    setNozzleForDeletion(nozzle);
    setIsDeleteNozzleDialogOpen(true);
  };

  const closeDeleteNozzleDialog = () => {
    setNozzleForDeletion(null);
    setIsDeleteNozzleDialogOpen(false);
  };

  const handleDeleteNozzleSuccess = () => {
    closeDeleteNozzleDialog();
    fetchNozzles();
  };

  // Mobile Card View
  const renderMobileView = () => (
    <Stack spacing={2} sx={{ mt: 2 }}>
      {nozzles.map((nozzle, index) => (
        <Card key={nozzle.id} variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Nozzle #{index + 1}
              </Typography>

              <FormControl fullWidth>
                <InputLabel>Nozzle Brand</InputLabel>
                <Select
                  value={nozzle.nozzleBrand}
                  label="Nozzle Brand"
                  error={hasNozzleValueChanged(nozzle, 'nozzleBrand')}
                  onChange={(e) => updateNozzle(nozzle.id, 'nozzleBrand', e.target.value)}
                >
                  <MenuItem value="TeeJet">TeeJet</MenuItem>
                  <MenuItem value="Wilger">Wilger</MenuItem>
                  <MenuItem value="MagnoJet">MagnoJet</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Nozzle Angle"
                type="text"
                inputMode="decimal"
                fullWidth
                value={nozzle.nozzleAngle}
                error={hasNozzleValueChanged(nozzle, 'nozzleAngle')}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || /^\d*\.?\d*$/.test(v)) updateNozzle(nozzle.id, 'nozzleAngle', v);
                }}
              />

              <TextField
                label="Nozzle GPM"
                type="text"
                inputMode="decimal"
                fullWidth
                value={nozzle.nozzleGpm}
                error={hasNozzleValueChanged(nozzle, 'nozzleGpm')}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || /^\d*\.?\d*$/.test(v)) updateNozzle(nozzle.id, 'nozzleGpm', v);
                }}
              />

              <TextField
                label="Spray Width (in)"
                type="text"
                inputMode="decimal"
                fullWidth
                value={nozzle.sprayWidth}
                error={hasNozzleValueChanged(nozzle, 'sprayWidth')}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || /^\d*\.?\d*$/.test(v)) updateNozzle(nozzle.id, 'sprayWidth', v);
                }}
              />

              <TextField
                label="Overlap (%)"
                type="text"
                inputMode="decimal"
                fullWidth
                value={nozzle.overlap}
                error={hasNozzleValueChanged(nozzle, 'overlap')}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || /^\d*\.?\d*$/.test(v)) updateNozzle(nozzle.id, 'overlap', v);
                }}
              />

              <FormControl fullWidth>
                <InputLabel>Boom</InputLabel>
                <Select
                  value={nozzle.tractorBoomId}
                  label="Boom"
                  error={hasNozzleValueChanged(nozzle, 'tractorBoomId')}
                  onChange={(e) => updateNozzle(nozzle.id, 'tractorBoomId', e.target.value)}
                >
                  {booms.map((boom) => (
                    <MenuItem key={boom.id} value={boom.id}>
                      {boom.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                fullWidth
                startIcon={<DeleteIcon />}
                disabled={!haveAnyValuesChanged()}
                onClick={() => openDeleteNozzleDialog(nozzle)}
              >
                Delete Nozzle
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );

  // Desktop Table View
  const renderDesktopView = () => (
    <TableContainer component={Paper} sx={{ my: 1 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell>Nozzle Brand</TableCell>
            <TableCell>Nozzle Angle</TableCell>
            <TableCell>Nozzle GPM</TableCell>
            <TableCell>Spray Width (in)</TableCell>
            <TableCell>Overlap (%)</TableCell>
            <TableCell>Boom</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {nozzles.map((nozzle, index) => (
            <TableRow key={nozzle.id}>
              <TableCell>{index + 1}</TableCell>
              <TableCell>
                <Select
                  value={nozzle.nozzleBrand}
                  error={hasNozzleValueChanged(nozzle, 'nozzleBrand')}
                  onChange={(e) => updateNozzle(nozzle.id, 'nozzleBrand', e.target.value)}
                >
                  <MenuItem value="TeeJet">TeeJet</MenuItem>
                  <MenuItem value="Wilger">Wilger</MenuItem>
                  <MenuItem value="MagnoJet">MagnoJet</MenuItem>
                </Select>
              </TableCell>
              <TableCell>
                <TextField
                  type="text"
                  inputMode="decimal"
                  value={nozzle.nozzleAngle}
                  error={hasNozzleValueChanged(nozzle, 'nozzleAngle')}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^\d*\.?\d*$/.test(v)) updateNozzle(nozzle.id, 'nozzleAngle', v);
                  }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  type="text"
                  inputMode="decimal"
                  value={nozzle.nozzleGpm}
                  error={hasNozzleValueChanged(nozzle, 'nozzleGpm')}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^\d*\.?\d*$/.test(v)) updateNozzle(nozzle.id, 'nozzleGpm', v);
                  }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  type="text"
                  inputMode="decimal"
                  value={nozzle.sprayWidth}
                  error={hasNozzleValueChanged(nozzle, 'sprayWidth')}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^\d*\.?\d*$/.test(v)) updateNozzle(nozzle.id, 'sprayWidth', v);
                  }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  type="text"
                  inputMode="decimal"
                  value={nozzle.overlap}
                  error={hasNozzleValueChanged(nozzle, 'overlap')}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^\d*\.?\d*$/.test(v)) updateNozzle(nozzle.id, 'overlap', v);
                  }}
                />
              </TableCell>
              <TableCell>
                <Select
                  value={nozzle.tractorBoomId}
                  error={hasNozzleValueChanged(nozzle, 'tractorBoomId')}
                  onChange={(e) => updateNozzle(nozzle.id, 'tractorBoomId', e.target.value)}
                >
                  {booms.map((boom) => (
                    <MenuItem key={boom.id} value={boom.id}>
                      {boom.name}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell>
                <Button
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  disabled={!haveAnyValuesChanged()}
                  onClick={() => openDeleteNozzleDialog(nozzle)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <Box className={styles.container} sx={{ pb: { xs: 3, sm: 2 } }}>
        <Typography variant="h4" sx={{ pt: { xs: 2, sm: 0 }, pb: 1 }}>
          {tractor.name}
        </Typography>
        <Typography variant="h5" sx={{ pb: 2 }}>
          Nozzle Management
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography>
              Add nozzles from left to right when looking at the boom from
              behind the sprayer.
            </Typography>
          </Box>
          {haveAnyValuesChanged() ? (
            <Button
              variant="outlined"
              disabled={loading}
              startIcon={<AddIcon />}
              onClick={openAddNozzleDialog}
              fullWidth={isMobile}
            >
              Add Nozzles
            </Button>
          ) : (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                variant="outlined"
                startIcon={<UndoIcon />}
                onClick={discardChanges}
                fullWidth={isMobile}
              >
                Discard Changes
              </Button>
              <Button
                variant="outlined"
                onClick={updateNozzles}
                startIcon={<SaveIcon />}
                fullWidth={isMobile}
              >
                Save Changes
              </Button>
            </Stack>
          )}
        </Stack>

        {isMobile ? renderMobileView() : renderDesktopView()}

        {addNozzleDialogOpen && (
          <AddNozzleDialog
            clientId={getClientId()}
            tractorId={getTractorId()}
            open={addNozzleDialogOpen}
            onClose={closeShowAddNozzleDialog}
            onSuccess={handleAddNozzlesSuccess}
          />
        )}
        {deleteNozzleDialogOpen && (
          <DeleteNozzleDialog
            clientId={getClientId()}
            nozzleId={nozzleForDeletion.id}
            open={deleteNozzleDialogOpen}
            onClose={closeDeleteNozzleDialog}
            onSuccess={handleDeleteNozzleSuccess}
          />
        )}
      </Box>
    </>
  );
};

export default AdminConfigTractorNozzles;